import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Topbar from '../components/Topbar';
import DarkCard from '../components/DarkCard';
import DarkButton from '../components/DarkButton';
import ThemeRoot from '../components/ThemeRoot';
import QuestionInput from '../components/QuestionInput';
import StageHomework from '../components/StageHomework';
import StageFeedback from '../components/StageFeedback';
import { CheckIcon } from '../svg/icons';
import { haptic } from '../lib/haptic';
import { useDarkStore } from '../store/darkStore';
import { useTokens } from '../../hooks/useTokens';
import { TOKEN_EARNINGS } from '../../types/tokens';
import { moderateText } from '../../utils/contentModeration';
import { useModeration } from '../../biz/hooks/useModeration';

const XP_PER_ANSWER = 10;
const XP_HOMEWORK_BONUS = 20;
const XP_FEEDBACK_BONUS = 15;

type Step =
  | { kind: 'question'; index: number }
  | { kind: 'homework' }
  | { kind: 'feedback' };

// ---------------------------------------------------------------------------
// Confetti burst — 32 coloured particles that fly outward from centre using
// CSS custom properties for their individual end-positions / rotation.
// ---------------------------------------------------------------------------
const CONFETTI_COLORS = ['#F59E0B', '#10B981', '#8B5CF6', '#EF4444', '#38BDF8', '#F472B6'];

function ConfettiParticles() {
  const particles = useMemo(
    () =>
      Array.from({ length: 32 }, (_, i) => {
        const angle = (i / 32) * 360;
        const dist = 70 + ((i * 11) % 90);
        return {
          color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
          tx: Math.cos((angle * Math.PI) / 180) * dist,
          ty: Math.sin((angle * Math.PI) / 180) * dist,
          rotation: (i * 53) % 720,
          width: 6 + (i % 4) * 2,
          height: i % 3 === 2 ? 14 : 7,
          delay: (i % 8) * 0.045,
          round: i % 5 === 0,
        };
      }),
    [],
  );

  return (
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden">
      {particles.map((p, i) => (
        <div
          key={i}
          className="absolute"
          style={
            {
              width: p.width,
              height: p.height,
              borderRadius: p.round ? '50%' : '2px',
              backgroundColor: p.color,
              animation: `confetti-fly 1.1s ${p.delay}s ease-out both`,
              '--tx': `${p.tx}px`,
              '--ty': `${p.ty}px`,
              '--rot': `${p.rotation}deg`,
            } as React.CSSProperties
          }
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pre-homework celebration popup
// ---------------------------------------------------------------------------
interface PreHomeworkPopupProps {
  emoji: string;
  stageName: string;
  explorerName: string;
  onDismiss: () => void;
}

function PreHomeworkPopup({ emoji, stageName, explorerName, onDismiss }: PreHomeworkPopupProps) {
  const PRAISE = [
    'You nailed it!',
    'Crushing it!',
    'That\'s the spirit!',
    'Boom — answers locked in!',
    'You\'re on fire!',
  ];
  const praise = PRAISE[Math.floor(Math.random() * PRAISE.length)];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/65 backdrop-blur-sm"
      onClick={onDismiss}
    >
      <div className="pop-in relative mx-4 w-full max-w-xs overflow-hidden rounded-3xl border border-amber/30 bg-surface p-8 text-center shadow-xl">
        <ConfettiParticles />
        <div className="relative z-10">
          <div className="mb-3 text-6xl leading-none">{emoji}</div>
          <div className="font-marker text-3xl text-amber">{praise}</div>
          <p className="mt-2 font-gill text-base text-ink/70">
            Great work, <span className="text-ink">{explorerName || 'founder'}</span>!
            <br />
            Now let's put{' '}
            <span className="text-amber">{stageName}</span> into practice.
          </p>
          <div className="mt-5 font-gill text-xs uppercase tracking-widest text-ink/30">
            tap anywhere to continue
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stage-complete confetti popup
// ---------------------------------------------------------------------------
interface StageCompletePopupProps {
  stageEmoji: string;
  stageName: string;
  stageId: number;
  nextStage: { id: number; name: string; emoji: string } | null;
  isFinalStage: boolean;
  onContinue: () => void;
}

function StageCompletePopup({
  stageEmoji,
  stageName,
  stageId,
  nextStage,
  isFinalStage,
  onContinue,
}: StageCompletePopupProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="pop-in relative mx-4 w-full max-w-xs overflow-hidden rounded-3xl border border-mint/30 bg-surface p-8 text-center shadow-xl">
        <ConfettiParticles />

        <div className="relative z-10">
          {/* Trophy SVG */}
          <div className="mb-4 flex justify-center">
            <svg width="72" height="72" viewBox="0 0 72 72" fill="none">
              <circle cx="36" cy="36" r="34" fill="#10B981" fillOpacity="0.15" stroke="#10B981" strokeOpacity="0.4" strokeWidth="1.5" />
              <text x="36" y="46" textAnchor="middle" fontSize="32">{stageEmoji}</text>
            </svg>
          </div>

          <div className="font-marker text-[11px] uppercase tracking-[0.22em] text-mint">
            Stage {stageId} Complete!
          </div>
          <div className="mt-1 font-marker text-2xl text-ink">{stageName}</div>

          {/* Stars row */}
          <div className="my-4 flex justify-center gap-1">
            {[0, 1, 2].map((i) => (
              <svg
                key={i}
                width="22"
                height="22"
                viewBox="0 0 24 24"
                style={{ animation: `confetti-fly 0s ${i * 0.12}s both` }}
              >
                <polygon
                  points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"
                  fill="#F59E0B"
                  stroke="#F59E0B"
                  strokeWidth="1"
                />
              </svg>
            ))}
          </div>

          {!isFinalStage && nextStage && (
            <div className="mb-5 rounded-2xl border border-amber/20 bg-amber/5 px-4 py-3">
              <div className="font-gill text-xs text-ink/50">Up next</div>
              <div className="mt-1 font-marker text-lg text-amber">
                {nextStage.emoji} {nextStage.name}
              </div>
            </div>
          )}

          <DarkButton accent={isFinalStage ? 'mint' : 'amber'} full onClick={onContinue}>
            {isFinalStage
              ? '🏁 See your journey'
              : `Start ${nextStage?.name ?? 'next stage'} →`}
          </DarkButton>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Challenge page
// ---------------------------------------------------------------------------
export default function Challenge() {
  const navigate = useNavigate();
  const { stageId } = useParams<{ stageId: string }>();
  const id = parseInt(stageId ?? '2', 10);

  const { stages, answers, explorerName, setAnswer, addXp, completeStage } = useDarkStore();
  const { earnTokens } = useTokens();
  const stage = stages.find((s) => s.id === id);

  const { moderate } = useModeration();

  const [stepIdx, setStepIdx] = useState(0);
  const [justSaved, setJustSaved] = useState(false);
  const [showPreHomework, setShowPreHomework] = useState(false);
  const [showStageComplete, setShowStageComplete] = useState(false);
  const [moderationError, setModerationError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  const preHomeworkTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset all per-stage state on stage id change (React Router reuses this component).
  useEffect(() => {
    setStepIdx(0);
    setShowPreHomework(false);
    setShowStageComplete(false);
    setModerationError(null);
    setChecking(false);
    if (preHomeworkTimer.current) {
      clearTimeout(preHomeworkTimer.current);
      preHomeworkTimer.current = null;
    }
  }, [id]);

  // Cleanup timer on unmount.
  useEffect(() => {
    return () => {
      if (preHomeworkTimer.current) clearTimeout(preHomeworkTimer.current);
    };
  }, []);

  const steps: Step[] = useMemo(() => {
    if (!stage) return [];
    const qSteps: Step[] = stage.questions.map((_, index) => ({
      kind: 'question',
      index,
    }));
    return [...qSteps, { kind: 'homework' }, { kind: 'feedback' }];
  }, [stage]);

  if (!stage || steps.length === 0) {
    return (
      <ThemeRoot>
        <Topbar />
        <div className="mx-auto max-w-xl px-4 py-10 text-center">
          <h1 className="font-marker text-3xl text-amber">No challenge here</h1>
          <DarkButton
            accent="amber"
            className="mt-6"
            onClick={() => navigate('/home')}
          >
            Back to Launchpad
          </DarkButton>
        </div>
      </ThemeRoot>
    );
  }

  const isFinalStage = id >= stages.length;
  const nextStage = stages.find((s) => s.id === id + 1) ?? null;

  const step = steps[stepIdx];
  const isLastStep = stepIdx === steps.length - 1;

  const currentQuestion =
    step.kind === 'question' ? stage.questions[step.index] : null;
  const currentValue = currentQuestion
    ? (answers[stage.key]?.[currentQuestion.id] ?? '')
    : '';
  const hasAnswer = currentValue.trim().length > 0;
  const canAdvance = currentQuestion
    ? currentQuestion.required
      ? hasAnswer
      : true
    : true;

  // Dismiss the pre-homework popup and advance to homework step.
  const dismissPreHomework = () => {
    if (preHomeworkTimer.current) {
      clearTimeout(preHomeworkTimer.current);
      preHomeworkTimer.current = null;
    }
    setShowPreHomework(false);
    setStepIdx((prev) => prev + 1);
  };

  const finishStage = () => {
    completeStage(id);
    earnTokens(TOKEN_EARNINGS.COMPLETE_STAGE, 'stage_complete', `Completed stage ${id + 1}`).catch(() => {});
    haptic('levelUp');
    setShowStageComplete(true);
  };

  const handleStageCompleteContinue = () => {
    setShowStageComplete(false);
    navigate(isFinalStage ? '/home/summary' : `/home/challenge/${id + 1}`);
  };

  const handleContinue = async () => {
    if (step.kind === 'question') {
      if (!canAdvance || checking) return;

      if (hasAnswer) {
        // Level 1 — instant client-side word list
        const check = moderateText(currentValue);
        if (!check.passed) {
          setModerationError("Hmm, that's not gonna fly. Keep it clean to move forward. 🚫");
          haptic('wrong');
          return;
        }

        // Level 2 — OpenAI via Cloud Function
        setChecking(true);
        const result = await moderate({
          text: currentValue,
          context: currentQuestion!.id,
          step: `stage_${stage.id}`,
          action: 'next',
        }).catch((): null => null);
        setChecking(false);

        if (!result) {
          setModerationError('Something went wrong. Please try again.');
          return;
        }
        if (result.blocked) {
          setModerationError('Your access is paused. Talk to the team to sort it out.');
          haptic('wrong');
          return;
        }
        if (result.flagged) {
          setModerationError("Hmm, that's not gonna fly. Keep it clean to move forward. 🚫");
          haptic('wrong');
          return;
        }

        setModerationError(null);
        addXp(XP_PER_ANSWER);
        haptic('correct');
        setJustSaved(true);
        setTimeout(() => setJustSaved(false), 700);
      } else {
        haptic('tap');
      }

      const isLastQuestion = step.index === stage.questions.length - 1;
      if (isLastQuestion) {
        // Show celebration popup before revealing the homework step.
        haptic('levelUp');
        setShowPreHomework(true);
        // Auto-advance after 2.5 s if the user doesn't tap first.
        preHomeworkTimer.current = setTimeout(dismissPreHomework, 2500);
      } else {
        setStepIdx(stepIdx + 1);
      }
      return;
    }

    if (step.kind === 'homework') {
      addXp(XP_HOMEWORK_BONUS);
      haptic('correct');
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 700);
      setStepIdx(stepIdx + 1);
      return;
    }

    // Feedback is the final step — award bonus + show stage-complete popup.
    addXp(XP_FEEDBACK_BONUS);
    finishStage();
  };

  const handleBack = () => {
    haptic('tap');
    setModerationError(null);
    if (stepIdx === 0) {
      navigate('/home');
      return;
    }
    setStepIdx(stepIdx - 1);
  };

  const headerRight =
    step.kind === 'question'
      ? `${step.index + 1} / ${stage.questions.length}`
      : step.kind === 'homework'
        ? 'Homework'
        : "KoKo's take";

  return (
    <ThemeRoot>
      <Topbar />

      {/* Pre-homework celebration popup */}
      {showPreHomework && (
        <PreHomeworkPopup
          emoji={stage.emoji}
          stageName={stage.name}
          explorerName={explorerName}
          onDismiss={dismissPreHomework}
        />
      )}

      {/* Stage-complete confetti popup */}
      {showStageComplete && (
        <StageCompletePopup
          stageEmoji={stage.emoji}
          stageName={stage.name}
          stageId={stage.id}
          nextStage={nextStage}
          isFinalStage={isFinalStage}
          onContinue={handleStageCompleteContinue}
        />
      )}

      {justSaved && (
        <div
          aria-hidden
          className="pointer-events-none fixed inset-0 z-10 bg-mint/15 mix-blend-screen"
          style={{ animation: 'fadeOut 700ms ease-out forwards' }}
        />
      )}

      <main className="mx-auto max-w-2xl px-4 py-6 md:py-10">
        <div className="mb-5 flex items-center justify-between">
          <div className="flex items-center gap-2 font-marker text-sm uppercase tracking-[0.2em] text-amber/90">
            <span className="text-base">{stage.emoji}</span>
            <span>
              Stage {stage.id} · {stage.name}
            </span>
          </div>
          <div className="font-gill text-sm text-gray-600 dark:text-ink/70">
            {headerRight}
          </div>
        </div>

        <div className="mb-5 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-surface">
          <div
            className="h-full rounded-full bg-amber transition-[width] duration-500"
            style={{ width: `${((stepIdx + 1) / steps.length) * 100}%` }}
          />
        </div>

        <DarkCard active>
          {step.kind === 'question' && currentQuestion && (
            <>
              <div className="flex items-center justify-between">
                <span className="rounded-full border border-amber/40 bg-amber/10 px-3 py-1 font-marker text-sm text-amber-700 dark:text-amber">
                  + {XP_PER_ANSWER} XP
                </span>
                <span className="font-gill text-[11px] uppercase tracking-widest text-gray-400 dark:text-ink/40">
                  {currentQuestion.required ? 'Required' : 'Optional'}
                </span>
              </div>

              <h2 className="mt-4 font-marker text-2xl leading-tight text-gray-900 dark:text-ink md:text-3xl">
                {currentQuestion.prompt}
              </h2>

              <div className="mt-6">
                <QuestionInput
                  question={currentQuestion}
                  value={currentValue}
                  onChange={(v) => {
                    setAnswer(stage.key, currentQuestion.id, v);
                    if (moderationError) setModerationError(null);
                  }}
                />
              </div>

              {moderationError ? (
                <div className="mt-5 rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 animate-spring-in">
                  <p className="font-gill text-sm text-red-400">{moderationError}</p>
                </div>
              ) : hasAnswer ? (
                <div className="mt-5 flex items-center gap-3 animate-spring-in">
                  <CheckIcon size={36} />
                  <div className="font-marker text-lg text-gray-900 dark:text-ink">
                    Nice — ready when you are.
                  </div>
                </div>
              ) : null}
            </>
          )}

          {step.kind === 'homework' && (
            <>
              <div className="flex items-center justify-between">
                <span className="rounded-full border border-violet/40 bg-violet/10 px-3 py-1 font-marker text-sm text-violet dark:text-violet-glow">
                  📓 Homework · + {XP_HOMEWORK_BONUS} XP
                </span>
                <span className="font-gill text-[11px] uppercase tracking-widest text-gray-400 dark:text-ink/40">
                  One more step after this
                </span>
              </div>
              <h2 className="mt-4 font-marker text-2xl leading-tight text-gray-900 dark:text-ink md:text-3xl">
                Let's put it to work
              </h2>
              <p className="mt-1 font-gill text-sm text-gray-600 dark:text-ink/70">
                Fill this in, then we'll have KoKo review everything.
              </p>

              <div className="mt-5">
                <StageHomework stageKey={stage.key} />
              </div>
            </>
          )}

          {step.kind === 'feedback' && (
            <>
              <div className="flex items-center justify-between">
                <span className="rounded-full border border-amber/40 bg-amber/10 px-3 py-1 font-marker text-sm text-amber-700 dark:text-amber">
                  🦊 Review · + {XP_FEEDBACK_BONUS} XP
                </span>
                <span className="font-gill text-[11px] uppercase tracking-widest text-gray-400 dark:text-ink/40">
                  Stage finisher
                </span>
              </div>
              <h2 className="mt-4 font-marker text-2xl leading-tight text-gray-900 dark:text-ink md:text-3xl">
                KoKo reviewed your answers
              </h2>
              <p className="mt-1 font-gill text-sm text-gray-600 dark:text-ink/70">
                Read through, then finish the stage when you're ready.
              </p>

              <div className="mt-5">
                <StageFeedback stageKey={stage.key} />
              </div>
            </>
          )}

          <div className="mt-6 flex items-center justify-between gap-3">
            <DarkButton accent="ghost" onClick={handleBack}>
              {stepIdx === 0 ? 'Quit' : 'Back'}
            </DarkButton>
            <DarkButton
              accent={isLastStep ? 'mint' : 'amber'}
              hapticKind={isLastStep ? 'levelUp' : 'tap'}
              disabled={!canAdvance || checking}
              onClick={handleContinue}
            >
              {checking
                ? 'Checking…'
                : isLastStep
                  ? 'Finish stage'
                  : step.kind === 'homework'
                    ? 'See KoKo review'
                    : 'Continue'}
            </DarkButton>
          </div>
        </DarkCard>
      </main>
    </ThemeRoot>
  );
}
